import { Suspense, lazy } from "react";
import type { ViewMode } from "../../../shared/types";
import type { useAppData } from "../../../hooks/useAppData";
import type { useAppFiltering } from "../../../hooks/useAppFiltering";
import type { useAppState } from "../../../hooks/useAppState";
import { QuickAdd } from "../../tasks/components/QuickAdd";
import { TaskList } from "../../tasks/components/TaskList";
import { WeekCalendar } from "../../calendar/components/WeekCalendar";
import { TodayView } from "../../dashboard/components/TodayView";
import { TemplatePanel } from "../../tasks/components/TemplatePanel";
import { HabitsView } from "../../habits/components/HabitsView";
import { AgendaView } from "../../calendar/components/agenda/AgendaView";
import { FreeSlotsView } from "../../calendar/components/free-slots/FreeSlotsView";
import { WeeklyView } from "../../dashboard/components/WeeklyView";
import { ActivityLog } from "../../dashboard/components/activity/ActivityLog";
import { ScheduleView } from "../../schedule/components/ScheduleView";
import { StatsView } from "../../dashboard/components/stats/StatsView";
const EisenhowerView = lazy(() =>
  import("../../eisenhower/components/EisenhowerView").then(
    (mod) => ({ default: mod.EisenhowerView }),
  ),
);
const JournalView = lazy(() =>
  import("../../journal/components/JournalView").then(
    (mod) => ({ default: mod.JournalView }),
  ),
);
const KanbanBoard = lazy(() =>
  import("../../kanban/components/KanbanBoard").then(
    (mod) => ({ default: mod.KanbanBoard }),
  ),
);

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <div className="w-8 h-8 rounded-full border-2 border-accent border-t-transparent animate-spin mx-auto mb-2" />
        <p className="text-sm text-ink-muted">Chargement...</p>
      </div>
    </div>
  );
}

interface AppViewRendererProps {
  readonly appData: ReturnType<typeof useAppData>;
  readonly filtering: ReturnType<typeof useAppFiltering>;
  readonly appState: ReturnType<typeof useAppState>;
  readonly setViewMode: (mode: ViewMode) => void;
}

export function AppViewRenderer({
  appData,
  filtering,
  appState,
  setViewMode,
}: AppViewRendererProps) {
  return {
    todayView: (
      <TodayView
        tasks={filtering.filteredTasks}
        events={filtering.filteredEvents}
        onCycleStatus={appData.cycleStatus}
        onDeleteTask={appData.deleteTask}
        onDeleteEvent={appData.deleteEvent}
        onUpdateEvent={appData.updateEvent}
        onQuickAdd={() => setViewMode("tasks")}
      />
    ),
    weekCalendar: (
      <WeekCalendar
        events={filtering.filteredEvents}
        tasks={filtering.filteredTasks}
        activeCategory={filtering.defaultCategory}
        googleCalendarUi={appState.googleCalendarUi}
        readOnly={appState.calendarReadOnly}
        onToggleReadOnly={appState.toggleCalendarReadOnly}
        onAddEvent={appData.addEvent}
        onAddEventFull={appData.addEventFull}
        onDeleteEvent={appData.deleteEvent}
        onUpdateEvent={appData.updateEvent}
        onDetachOccurrence={appData.detachOccurrence}
        onAddRecurrenceException={appData.addRecurrenceException}
        onResyncOccurrence={appData.resyncOccurrence}
        onResyncAllOccurrences={appData.resyncAllOccurrences}
        onUndoEvent={appData.undoEvent}
      />
    ),
    quickAdd: (
      <QuickAdd
        categoryFilter={appState.activeFilter}
        onAdd={appData.addTask}
      />
    ),
    taskList: (
      <TaskList
        tasks={filtering.filteredTasks}
        showCategory={appState.activeFilter === "all"}
        onCycleStatus={appData.cycleStatus}
        onUpdate={appData.updateTask}
        onDelete={appData.deleteTask}
        onAddSubtask={appData.addSubtask}
        onToggleSubtask={appData.toggleSubtask}
        onDeleteSubtask={appData.deleteSubtask}
        onReorder={appData.reorderTask}
      />
    ),
    habitsView: <HabitsView appState={appState} />,
    agendaView: (
      <AgendaView
        events={filtering.filteredEvents}
        tasks={filtering.filteredTasks}
        onCycleStatus={appData.cycleStatus}
        onDeleteEvent={appData.deleteEvent}
        onUpdateEvent={appData.updateEvent}
        onAddRecurrenceException={appData.addRecurrenceException}
      />
    ),
    freeSlotsView: <FreeSlotsView events={filtering.expandedEvents} />,
    weeklyView: <WeeklyView events={filtering.visibleEvents} />,
    statsView: (
      <StatsView tasks={appData.activeTasks} events={appData.events} />
    ),
    activityLog: (
      <ActivityLog
        tasks={appData.activeTasks}
        events={appData.events}
      />
    ),
    templatePanel: (
      <TemplatePanel
        onAddTask={appData.addTask}
        onAddEvent={appData.addEvent}
      />
    ),
    scheduleView: (
      <ScheduleView
        events={appData.events}
        categoryFilter={appState.activeFilter}
        defaultCategory={filtering.defaultCategory}
        onUpdateEvent={appData.updateEvent}
        onAddEventFull={appData.addEventFull}
        onDeleteEvent={appData.deleteEvent}
      />
    ),
    eisenhowerView: (
      <Suspense fallback={<LoadingFallback />}>
        <EisenhowerView
          tasks={filtering.filteredTasks}
          onCycleStatus={appData.cycleStatus}
          onDeleteTask={appData.deleteTask}
        />
      </Suspense>
    ),
    journalView: (
      <Suspense fallback={<LoadingFallback />}>
        <JournalView />
      </Suspense>
    ),
    kanbanView: (
      <Suspense fallback={<LoadingFallback />}>
        <KanbanBoard
          tasks={filtering.filteredTasks}
          onCycleStatus={appData.cycleStatus}
          onDeleteTask={appData.deleteTask}
          onUpdateTask={appData.updateTask}
        />
      </Suspense>
    ),
  };
}
